// Task #993: install the X-Client fetch interceptor as the very first
// side effect in the entry module, BEFORE any other module is imported.
// ES module evaluation is top-down, so by importing this bootstrap
// module first we guarantee `window.fetch` is patched before any
// downstream module's top-level code (Bugsnag, App, hooks, services)
// can issue a fetch. This is the strongest install-order guarantee we
// can give in pure ESM.
import "@/lib/installClientHeader";

import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { BugsnagErrorBoundary, notifyBugsnag } from "@/lib/bugsnag";
import ReviewCard from "@/components/ReviewCard";
import RestaurantPostCard from "@/components/RestaurantPostCard";
import { KudozModule } from "@/components/KudozModule";
import { KudozCelebration } from "@/components/KudozCelebration";
import { InlineKudozDelta } from "@/components/InlineKudozDelta";
import { logger } from "@/lib/logger";

function validateCriticalExports(): void {
  const checks: Array<{ name: string; value: unknown }> = [
    { name: "ReviewCard", value: ReviewCard },
    { name: "RestaurantPostCard", value: RestaurantPostCard },
    { name: "KudozModule", value: KudozModule },
    { name: "KudozCelebration", value: KudozCelebration },
    { name: "InlineKudozDelta", value: InlineKudozDelta },
  ];

  for (const { name, value } of checks) {
    if (value === undefined || value === null) {
      const msg = `[STARTUP] Critical export is undefined: ${name}`;
      logger.error(msg);
      notifyBugsnag(new Error(msg), {
        component: name,
        stage: "startup-export-guard",
      });
    }
  }
}

validateCriticalExports();

function AppCrashFallback({ error }: { error: Error }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: 16,
        padding: 24,
      }}
    >
      <h2 style={{ margin: 0 }}>Something went wrong</h2>
      <p style={{ color: "#666", textAlign: "center", margin: 0 }}>
        {error?.message || "An unexpected error occurred"}
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: "8px 20px", cursor: "pointer" }}
      >
        Reload page
      </button>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <BugsnagErrorBoundary FallbackComponent={AppCrashFallback}>
    <App />
  </BugsnagErrorBoundary>
);
